import { useState } from 'react';
import { Card, Button, Badge } from 'react-bootstrap';

// Who changed this recipe, what they changed, and when.
//
// A standard is the instruction for making a cable, and until 2026-10-12 it
// could be retyped with no trace anywhere. Since then every edit is recorded
// twice: an `edits[]` entry on the document itself (last 50, `{ at, by, reason,
// changes }`) and an `EDIT` event in the factory feed, which is what the
// production and sales dashboard timelines show.
//
// The feed answers "what happened in the factory today". This answers the
// question somebody has while looking AT the recipe — "why does this say 0.8?" —
// and that is a different question, asked in a different place.
//
// Lives in its own file because it belongs in BOTH places a standard is read:
// the overview page (`/apps/standard/:id`) and the edit modal opened from the
// list. A second copy would be a second thing to keep in step.

/** Local time, because a recipe is edited by somebody standing here. */
const editStamp = at => {
  const d = at ? new Date(at) : null;
  if (!d || Number.isNaN(d.getTime())) return '—';
  return d.toLocaleString();
};

/** One side of a change, with an empty one spelt out rather than left blank. */
const sideOf = v => {
  if (v === null || v === undefined || v === '') return '∅';
  if (typeof v === 'object') return JSON.stringify(v);
  return String(v);
};

const StandardEditHistory = ({ edits, className = 'mb-3' }) => {
  const [open, setOpen] = useState(false);
  // Newest first: the last change is the one being asked about.
  const list = Array.isArray(edits) ? [...edits].reverse() : [];

  if (!list.length) {
    return (
      <Card className={className}>
        <Card.Body className="py-2 fs-9 text-700">
          No recorded edits. Every change made from here is logged from now on —
          who, what and when.
        </Card.Body>
      </Card>
    );
  }

  const shown = open ? list : list.slice(0, 3);

  return (
    <Card className={className}>
      <Card.Body className="py-2">
        <div className="d-flex align-items-center gap-2 mb-2">
          <h6 className="mb-0">Edit history</h6>
          <Badge bg="secondary" className="fs-10">
            {list.length}
          </Badge>
          {list.length > 3 && (
            <Button
              variant="link"
              className="p-0 fs-10 ms-auto"
              onClick={() => setOpen(o => !o)}
            >
              {open ? 'show the last 3' : `show all ${list.length}`}
            </Button>
          )}
        </div>

        {shown.map((e, i) => (
          <div
            key={`${e?.at || i}-${i}`}
            className={`fs-10 ${i ? 'mt-2 pt-2 border-top' : ''}`}
          >
            <div className="d-flex flex-wrap align-items-center gap-2">
              <span className="fw-semibold text-900">{e?.by || 'unknown'}</span>
              <span className="text-700">{editStamp(e?.at)}</span>
              {e?.reason ? <span className="text-700">· {e.reason}</span> : null}
            </div>
            <div className="mt-1">
              {(Array.isArray(e?.changes) ? e.changes : []).map((c, k) => (
                <div key={k} className="text-700 font-monospace">
                  {/* `path` is what utils/standardDiff.js writes —
                      `phases.exterior_sheat.density`. */}
                  {c?.path || c?.field || '?'}:{' '}
                  <span className="text-danger">{sideOf(c?.from)}</span>
                  {' → '}
                  <span className="text-success">{sideOf(c?.to)}</span>
                </div>
              ))}
              {!(Array.isArray(e?.changes) && e.changes.length) && (
                <div className="text-700">no field-level detail recorded</div>
              )}
            </div>
          </div>
        ))}
      </Card.Body>
    </Card>
  );
};

export default StandardEditHistory;
